/**
 * ThemeEditor - the shared design-token editing surface (colour pickers, font
 * selects, live preview, save / reset-to-defaults).
 *
 * Presentational + self-contained draft state: the parent owns data fetching
 * and persistence and passes the saved `theme` overrides in, plus an `onSave`
 * that receives the sparse override map. Used by both the org-admin Settings >
 * Platform > Appearance modal (org-scoped endpoint) and the staff tenant view
 * (staff endpoint), so the two never drift - they edit the same record
 * (`Tenant.settings["theme"]`) through the same UI.
 *
 * Layout is container-query driven (`@container` on the token list), so the
 * same component reads correctly full-width on the staff page and in the
 * narrower settings modal: a row lays label/control side by side when there is
 * room and stacks vertically when there is not - or unconditionally when the
 * token's own label + hint copy is long enough to wrap either way.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { RotateCcw } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/shared/components/ui/searchable-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  THEME_TOKENS,
  themeBaseline,
  type ThemeOverrides,
  type ThemeTokenDef,
} from '@/shared/lib/theme'
import {
  BUNDLED_FONT_OPTIONS,
  CURATED_GOOGLE_FONTS,
  fontStack,
  GOOGLE_FAMILY_TO_FONT,
  isBundledOrSystemFamily,
  loadGoogleFont,
  primaryFamily,
} from '@/shared/lib/fonts'

type TokenGroup = ThemeTokenDef['group']

const GROUP_ORDER: TokenGroup[] = ['Brand', 'Surface', 'Text', 'Typography']

// Font picker groups: platform-bundled defaults pinned on top, then the curated
// Google Fonts grouped by category. `value` is the full font-family stack (the
// stored token value); labels stay searchable by family name and category.
const CATEGORY_LABELS: Record<string, string> = {
  'sans-serif': 'Sans serif',
  serif: 'Serif',
  display: 'Display',
  handwriting: 'Handwriting',
  monospace: 'Monospace',
}
const CATEGORY_ORDER = [
  'sans-serif',
  'serif',
  'display',
  'handwriting',
  'monospace',
]

const FONT_GROUPS: SearchableSelectGroup[] = [
  {
    label: 'Platform defaults',
    options: BUNDLED_FONT_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label,
      keywords: ['default', 'platform', 'bundled'],
    })),
  },
  ...CATEGORY_ORDER.map((category) => ({
    label: CATEGORY_LABELS[category],
    options: CURATED_GOOGLE_FONTS.filter((f) => f.category === category).map(
      (f) => ({
        value: fontStack(f),
        label: f.family,
        keywords: [category, 'google'],
      }),
    ),
  })),
]

/** Human label for a stored font value (bundled label, else the family name). */
function fontValueLabel(value: string): string {
  const bundled = BUNDLED_FONT_OPTIONS.find((o) => o.value === value)
  if (bundled) return bundled.label
  return primaryFamily(value) || 'Custom font'
}

/** Is this exact font-family stack one of the picker's catalogue entries? */
function isCatalogueFont(value: string): boolean {
  return FONT_GROUPS.some((g) => g.options.some((o) => o.value === value))
}

/**
 * Picker groups for one font row.
 *
 * A tenant's font can be a stack the catalogue does not contain - a brand font
 * written by that tenant's configuration carries its own fallbacks (e.g.
 * `"Oswald", "Arial Narrow", Arial, sans-serif`) which never string-matches the
 * catalogue's `"Oswald", sans-serif`. The picker resolves its trigger label by
 * exact value match, so such a value used to render as an empty "Select..."
 * even though a font was very much in use. Surfacing the current value as a
 * real (pinned) option fixes the blank trigger and keeps it selectable after
 * the admin browses away from it.
 */
function fontGroupsFor(value: string): SearchableSelectGroup[] {
  if (!value || isCatalogueFont(value)) return FONT_GROUPS
  return [
    {
      label: 'In use',
      options: [
        {
          value,
          label: fontValueLabel(value),
          keywords: ['current', 'in use', 'tenant', 'brand'],
        },
      ],
    },
    ...FONT_GROUPS,
  ]
}

/**
 * Copy long enough that the row reads better stacked at any width. Measured
 * against `label + hint`; only the genuinely verbose tokens cross it, since
 * width-driven stacking is already handled by the container query.
 */
const STACK_TEXT_THRESHOLD = 60

function stacksControl(token: ThemeTokenDef): boolean {
  return token.label.length + token.hint.length > STACK_TEXT_THRESHOLD
}

/**
 * True when a font value is a *custom web font* - a family that must be served
 * from its own font files to render, as opposed to a platform-bundled/system
 * family or one of the Google fonts the picker already offers and the runtime
 * loader fetches on its own.
 *
 * This is the only font provenance worth flagging: a tenant whose theme was
 * seeded with, say, `"Oswald", ...` picks a plain Google font (nothing to
 * indicate - it just works), whereas one seeded with a licensed brand face the
 * platform does not host needs the admin to know it depends on external font
 * files.
 *
 * Two positive exclusions, then flag the remainder:
 *   1. bundled (@fontsource) / generic / OS families resolve locally;
 *   2. curated Google families are a standard picker option, fetched at runtime;
 *   3. anything else is a family the platform neither bundles nor curates, so it
 *      renders only if the tenant provides it as a hosted web font -> flag it.
 * (A non-curated Google family also lands in case 3; from a CSS stack alone it
 * is indistinguishable from a self-hosted face, and both equally warrant the
 * "provide the font" signal.)
 */
function isHostedWebFont(value: string): boolean {
  const family = primaryFamily(value)
  if (!family) return false
  if (isBundledOrSystemFamily(value)) return false
  if (GOOGLE_FAMILY_TO_FONT.has(family.toLowerCase())) return false
  return true
}

/** Small chip marking a font row whose value is a custom hosted web font. */
function WebFontBadge() {
  return (
    <Badge
      variant="secondary"
      title="A custom web font set for your tenant. It renders from hosted font files rather than one of the standard picker fonts."
      className="px-1.5 py-0 text-[9px] font-normal uppercase tracking-[0.1em]"
    >
      Web font
    </Badge>
  )
}

function ResetButton({
  isOverridden,
  onReset,
}: {
  isOverridden: boolean
  onReset: () => void
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      title="Reset to default"
      onClick={onReset}
      disabled={!isOverridden}
      className="shrink-0"
    >
      <RotateCcw size={13} />
    </Button>
  )
}

/**
 * One token row: label + hint + provenance chip on one side, the control and
 * its reset button on the other. Side by side when there is room, stacked when
 * the container is narrow or the copy is long (see `stacksControl`).
 */
function TokenRow({
  token,
  isOverridden,
  badge,
  onReset,
  children,
}: {
  token: ThemeTokenDef
  isOverridden: boolean
  /** Optional chip shown beside the label (e.g. the font web-font marker). */
  badge?: ReactNode
  onReset: () => void
  children: ReactNode
}) {
  const stacked = stacksControl(token)
  return (
    <div
      data-testid={`theme-row-${token.key.replace(/^--/, '')}`}
      data-stacked={stacked ? 'always' : 'responsive'}
      className={cn(
        'flex gap-x-3 gap-y-1.5',
        stacked ? 'flex-col' : 'flex-col @md:flex-row @md:items-center',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Label className="text-[13px] text-[var(--ink)]">{token.label}</Label>
          {badge}
        </div>
        <p className="text-[10px] leading-snug text-[var(--ink-3)]">{token.hint}</p>
      </div>
      <div
        className={cn(
          'flex shrink-0 items-center gap-3',
          // A stacked row still ends on the same right edge as the inline rows
          // above and below it, so a group reads as one column of controls
          // instead of alternating left/right.
          stacked ? 'w-full justify-end' : 'w-full @md:w-auto',
        )}
      >
        {children}
        <ResetButton isOverridden={isOverridden} onReset={onReset} />
      </div>
    </div>
  )
}

/**
 * The value to display/edit for a token: the saved override, else the baseline.
 *
 * Brand (`--brand`) and accent (`--green`) mirror each other - an unset one
 * shows the other's value - matching `resolveBrandAccent` at apply time so the
 * editor preview and the live app agree.
 */
function effectiveValue(
  draft: ThemeOverrides,
  baseline: ThemeOverrides,
  token: ThemeTokenDef,
): string {
  if (token.key === '--green') {
    return (
      draft['--green'] ?? draft['--brand'] ?? baseline['--green'] ?? token.default
    )
  }
  if (token.key === '--brand') {
    return (
      draft['--brand'] ?? draft['--green'] ?? baseline['--brand'] ?? token.default
    )
  }
  return draft[token.key] ?? baseline[token.key] ?? token.default
}

/** Build a sparse override map: only tokens whose value differs from baseline. */
function toSparse(draft: ThemeOverrides, baseline: ThemeOverrides): ThemeOverrides {
  const out: ThemeOverrides = {}
  for (const token of THEME_TOKENS) {
    const value = draft[token.key]
    if (value && value !== baseline[token.key]) out[token.key] = value
  }
  return out
}

interface RowProps {
  token: ThemeTokenDef
  value: string
  isOverridden: boolean
  onChange: (value: string) => void
  onReset: () => void
}

function ColorRow({ token, value, isOverridden, onChange, onReset }: RowProps) {
  return (
    <TokenRow token={token} isOverridden={isOverridden} onReset={onReset}>
      <Input
        type="color"
        aria-label={`${token.label} colour`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 shrink-0 cursor-pointer p-1"
      />
      <Input
        aria-label={`${token.label} hex`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full min-w-0 font-mono text-[12px] @md:w-[110px]"
      />
    </TokenRow>
  )
}

function FontRow({ token, value, isOverridden, onChange, onReset }: RowProps) {
  // Rebuilt per value so a non-catalogue stack (a seeded brand font) is pinned
  // as the "In use" option and the trigger always shows what is applied.
  const groups = useMemo(() => fontGroupsFor(value), [value])
  return (
    <TokenRow
      token={token}
      isOverridden={isOverridden}
      badge={isHostedWebFont(value) ? <WebFontBadge /> : undefined}
      onReset={onReset}
    >
      <SearchableSelect
        value={value}
        onValueChange={onChange}
        groups={groups}
        searchPlaceholder="Search fonts..."
        emptyMessage="No fonts match"
        // An explicit width, not `min-w-`: the picker's own trigger class
        // carries `w-full`, so a min-width let it grow past the row and push
        // the reset button outside the control column.
        triggerClassName="min-w-0 flex-1 text-[12px] @md:w-[230px] @md:flex-none"
        data-testid={`theme-font-${token.key.replace(/^--font-/, '')}`}
        renderValue={() => (
          <span className="truncate" style={{ fontFamily: value }}>
            {fontValueLabel(value)}
          </span>
        )}
      />
    </TokenRow>
  )
}

export function ThemeEditor({
  theme,
  seedKey,
  saving,
  scopeNote,
  variant = 'page',
  onSave,
}: {
  /** The tenant's saved sparse override map. */
  theme: ThemeOverrides
  /** Reset the editable draft when this changes (org/tenant switch). */
  seedKey: string
  saving: boolean
  /** Overrides the intro copy (the modal states the tenant-wide scope). */
  scopeNote?: ReactNode
  /**
   * `'page'` (default) grows to its content - the staff tab, which scrolls with
   * the page. `'dialog'` fills a height-bounded host instead: the token list
   * scrolls in its own column with the save row pinned beneath it, and the
   * preview sits at the top of its column rather than stretching, so a modal
   * stays a sensible height with no dead space beside the preview.
   */
  variant?: 'page' | 'dialog'
  /** Persist the sparse override map ({} clears all overrides). */
  onSave: (theme: ThemeOverrides) => void
}) {
  const [draft, setDraft] = useState<ThemeOverrides>(theme ?? {})
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)

  // Baseline shown before any override: the platform (iWarranty) defaults. An
  // un-themed tenant renders exactly this, so the editor shows it too.
  const baseline = useMemo(() => themeBaseline(), [])

  // Seeding the editable draft from the saved `theme`, reconciled against the
  // persistent (stale-while-revalidate) cache the data arrives through:
  //
  //  - First load for a tenant/org (seedKey) adopts whatever `theme` is.
  //  - A later `theme` change is a revalidation. Adopt it ONLY while the draft
  //    still equals what we last seeded (the user has not edited) - so a fresh
  //    server value (a staff edit, a deploy reseed) replaces a stale cached one
  //    instead of the admin editing and saving stale data. Once the draft
  //    diverges, keep the user's unsaved edits untouched.
  const seededJsonRef = useRef<string | null>(null)
  useEffect(() => {
    seededJsonRef.current = null
  }, [seedKey])
  useEffect(() => {
    const incoming = theme ?? {}
    const incomingJson = JSON.stringify(incoming)
    if (seededJsonRef.current === incomingJson) return
    if (seededJsonRef.current === null) {
      seededJsonRef.current = incomingJson
      setDraft(incoming)
      return
    }
    const seeded = seededJsonRef.current
    seededJsonRef.current = incomingJson
    setDraft((prev) => (JSON.stringify(prev) === seeded ? incoming : prev))
  }, [theme, seedKey])

  const grouped = useMemo(() => {
    const map = new Map<TokenGroup, ThemeTokenDef[]>()
    for (const token of THEME_TOKENS) {
      const list = map.get(token.group) ?? []
      list.push(token)
      map.set(token.group, list)
    }
    return map
  }, [])

  // Live preview: the working draft applied as CSS variables on a contained
  // wrapper (runtime-computed dynamic values). Empty values are skipped so the
  // CSS fallback baked into the component still applies.
  const previewStyle = useMemo(() => {
    const style: Record<string, string> = {}
    for (const token of THEME_TOKENS) {
      const value = effectiveValue(draft, baseline, token)
      if (value) style[token.key] = value
    }
    return style as CSSProperties
  }, [draft, baseline])

  // Fetch the Google font behind each font token in the draft so the preview
  // (and the picker trigger) render in the selected family, not just its
  // fallback. `loadGoogleFont` is deduped and a no-op for bundled/system fonts.
  useEffect(() => {
    for (const token of THEME_TOKENS) {
      if (token.type !== 'font') continue
      loadGoogleFont(effectiveValue(draft, baseline, token))
    }
  }, [draft, baseline])

  function setToken(key: string, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  function resetToken(token: ThemeTokenDef) {
    setDraft((prev) => {
      const next = { ...prev }
      delete next[token.key]
      return next
    })
  }

  function handleResetAll() {
    setDraft({})
    setResetConfirmOpen(false)
    onSave({})
  }

  const hasOverrides = Object.keys(toSparse(draft, baseline)).length > 0
  const inDialog = variant === 'dialog'

  const tokenList = (
    <>
      <p className="text-[12px] leading-relaxed text-[var(--ink-3)]">
        {scopeNote ?? (
          <>
            These tokens default to the platform theme until you change them.
            Edit a colour or font to override it for this organisation; reset any
            token to fall back to the default.
          </>
        )}
      </p>

      {GROUP_ORDER.map((group) => {
        const tokens = grouped.get(group) ?? []
        if (!tokens.length) return null
        return (
          <div key={group}>
            <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
              {group}
            </h3>
            <div className="space-y-4">
              {tokens.map((token) => {
                const value = effectiveValue(draft, baseline, token)
                const isOverridden = draft[token.key] != null
                const rowProps = {
                  token,
                  value,
                  isOverridden,
                  onChange: (v: string) => setToken(token.key, v),
                  onReset: () => resetToken(token),
                }
                if (token.type === 'font')
                  return <FontRow key={token.key} {...rowProps} />
                return <ColorRow key={token.key} {...rowProps} />
              })}
            </div>
          </div>
        )
      })}
    </>
  )

  const actions = (
    <div className="flex items-center justify-between border-t-[0.5px] border-t-[var(--rule)] pt-5">
      <Button
        type="button"
        variant="ghost"
        data-testid="theme-reset-all-btn"
        onClick={() => setResetConfirmOpen(true)}
        disabled={saving || !hasOverrides}
        className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
      >
        Reset all
      </Button>
      <Button
        data-testid="theme-save-btn"
        onClick={() => onSave(toSparse(draft, baseline))}
        disabled={saving}
        className="h-auto rounded-[4px] bg-[var(--green)] px-5 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
      >
        {saving ? 'Saving...' : 'Save changes'}
      </Button>
    </div>
  )

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-10 lg:flex-row',
          inDialog && 'h-full min-h-0 gap-8',
        )}
      >
        {/* Editor. `@container` so each token row can lay itself out against
            THIS column's width - which is what actually differs between the
            full-width staff page and the narrower settings modal - rather than
            the viewport. */}
        <div
          className={cn(
            '@container max-w-lg flex-1',
            inDialog ? 'flex min-h-0 flex-col gap-5' : 'space-y-8',
          )}
        >
          {inDialog ? (
            // Only the token list scrolls; the save row stays put beneath it.
            <div className="min-h-0 flex-1 space-y-8 overflow-y-auto pr-2">
              {tokenList}
            </div>
          ) : (
            tokenList
          )}
          {actions}
        </div>

        {/* Live preview */}
        <div className={cn('flex-1', inDialog && 'lg:self-start')}>
          <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
            Preview
          </h3>
          <div
            data-testid="theme-preview"
            style={previewStyle}
            className="overflow-hidden rounded-lg border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] font-sans"
          >
            {/* Brand-focus surface (login background / welcome hero) */}
            <div className="bg-[var(--brand)] px-6 py-8">
              <p
                data-testid="theme-preview-heading"
                className="font-display text-[22px] leading-tight text-white"
              >
                Brand surface
              </p>
              <p className="text-[11px] text-white/70">
                Login background &amp; welcome hero use the brand colour.
              </p>
            </div>
            <div className="space-y-4 p-6">
              <p className="font-display text-[28px] leading-tight text-[var(--ink)]">
                A warranty, kept simple.
              </p>
              <p className="text-[13px] leading-relaxed text-[var(--ink-2)]">
                Body copy renders in your selected font. This panel previews your
                brand colour, surfaces, and text tones together.
              </p>
              <p className="text-[11px] text-[var(--ink-3)]">
                Muted captions and helper text look like this.
              </p>
              <Input placeholder="Sample input field" readOnly />
              <div className="flex items-center gap-2 pt-1">
                <Button className="h-auto bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)]">
                  Primary action
                </Button>
                <Button
                  variant="outline"
                  className="h-auto px-4 py-2 text-[11px] uppercase tracking-[0.1em]"
                >
                  Secondary
                </Button>
              </div>
              <div className="rounded-md bg-[var(--bg-subtle)] p-3">
                <p className="text-[12px] text-[var(--ink-2)]">
                  A subtle surface card, tinted by your background tokens.
                </p>
              </div>
              <span className="inline-block rounded-full bg-[var(--gold-bg)] px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-[var(--gold-ink)]">
                Accent badge
              </span>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent data-testid="theme-reset-confirm" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset theme to defaults?</DialogTitle>
            <DialogDescription>
              This clears all colour and font overrides and restores the platform
              defaults. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="theme-reset-confirm-btn"
              onClick={handleResetAll}
              disabled={saving}
            >
              {saving ? 'Resetting...' : 'Reset all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
